import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  Signal,
  viewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AgentStepModel,
  AiService,
  StepType,
  ToolRequestStep,
  ToolResponseStep,
} from 'src/app/models/api/agentStep.model';
import { CitedSource } from '../../enterprise-ai-agent-step/ai-agent-citation.component';
import { resolveCitationResourceType } from '../../enterprise-ai-agent-step/citation-utils';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import {
  AiAgentStepsHttpService,
  SensitiveDataRequestActions,
} from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { ActivityMessageComponent } from './message/activity-message.component';
import { ActivityMessage, SensitiveDataType } from './activity-message.types';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { AvatarModule } from 'primeng/avatar';
import { AvatarStylePipe } from 'src/app/pipes/avatar-style/avatar-style.pipe';
import moment from 'moment';
import { Severities } from 'src/app/components/severities-cell/severities-cell.component';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { LucideIconsModule } from 'src/app/shared/icons/lucide-icons.module';
import { Label } from 'src/app/labels/englishLabels';

interface UserMessage {
  id: string;
  userName: string;
  timestamp: Date;

  service: undefined;
  logoPath: undefined;
}

interface ServiceMessage {
  id: string;
  service: AiService;
  logoPath: string;
  timestamp: Date;

  userName: undefined;
}

type Message = UserMessage | ServiceMessage;

interface ActivitiesGroupedByDate {
  messages: ActivityMessage[];
  details: Message;
  sources: CitedSource[];
  citedSources: CitedSource[];
}

@Component({
  selector: 'app-activities-messages',
  standalone: true,
  templateUrl: './activities-messages.component.html',
  styleUrls: ['./activities-messages.component.scss'],
  imports: [
    CommonModule,
    ActivityMessageComponent,
    AvatarModule,
    AvatarStylePipe,
    MatButtonModule,
    MatMenuModule,
    LucideIconsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivitiesMessagesComponent {
  activities = input.required<AgentStepModel[]>();
  findingsByActivity = input.required<Record<AgentStepModel['id'], Severities>>();
  findingIdsByActivity = input<Record<AgentStepModel['id'], string[]>>({});
  showDeleteAction = input<boolean>(false);
  findingsForActivityClicked = output<string>();
  deleteThreadClicked = output<void>();

  protected Label = Label;

  activitiesGroupedByDate: Signal<Record<string, ActivitiesGroupedByDate[]>> = computed(() =>
    this._getActivitiesGroupedByDate()
  );

  stepMessages = viewChildren(ActivityMessageComponent);
  stepMessagesById = computed(() => {
    const messagesById: Record<string, ActivityMessageComponent> = {};
    for (const message of this.stepMessages()) {
      messagesById[message.id()] = message;
    }
    return messagesById;
  });

  constructor(
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _aiAgentStepsService: AiAgentStepsHttpService,
    private readonly _sidePanel: SidePanelService
  ) {}

  public scrollToStep(stepId: string): void {
    this.stepMessagesById()[stepId]?.scrollToMessage();
  }

  private _getFirstFindingIdForStep(stepId: string): string | undefined {
    const findingIds = this.findingIdsByActivity()[stepId];
    return findingIds?.[0];
  }

  private _getActivitiesGroupedByDate(): Record<string, ActivitiesGroupedByDate[]> {
    const result: Record<string, ActivitiesGroupedByDate[]> = {};

    this.activities().forEach((activity) => {
      let details: Message;
      if (activity.type === StepType.userMessageStep) {
        details = {
          id: activity.id,
          userName: activity.mainActor.displayName,
          timestamp: new Date(activity.timestamp),
        } as UserMessage;
      } else {
        details = {
          id: activity.id,
          service: activity.service,
          logoPath: getAiServiceLogoPath(activity.service),
          timestamp: new Date(activity.timestamp),
        } as ServiceMessage;
      }

      const { messages, citedSources, sources } = this._getMessages(activity);

      const activityGroupedByDate = {
        messages,
        sources,
        citedSources,
        details,
      };

      const dateTime = moment(details.timestamp).format('DD MMM YYYY');
      if (!result[dateTime]) {
        result[dateTime] = [];
      }

      result[dateTime].push(activityGroupedByDate);
    });

    for (const activity in result) {
      result[activity].sort((a, b) => a.details.timestamp.getTime() - b.details.timestamp.getTime());
    }

    return Object.fromEntries(
      Object.entries(result).sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime())
    );
  }

  private _getMessages(step: AgentStepModel) {
    const messages: ActivityMessage[] = [];
    const citedSources: CitedSource[] = [];
    const sources: CitedSource[] = [];

    switch (step.type) {
      case StepType.aiMessageStep:
      case StepType.userMessageStep:
      case StepType.agentHandoffRequestStep:
      case StepType.agentHandoffResponseStep:
        messages.push({
          key: '',
          value: '',
          sensitiveData: {
            type: SensitiveDataType.SNIPPET,
          },
        });
        if (step.service === AiService.chatGptEnterprise) {
          // this.attachmentActions = [this.downloadAttachmentAction, this.deleteAttachmentAction];
        } else {
          // this.attachmentActions = [];
        }
        if ('attachedDocuments' in step && step.attachedDocuments?.length) {
          // this.attachments.set(step.attachedDocuments);
          // this.stepDetailsCardFields.push(this.attachmentCardField());
        }
        break;

      case StepType.ragStep:
        switch (step.service) {
          case AiService.m365Copilot:
            for (const source of step.citedSources) {
              const resourceType = resolveCitationResourceType(source, step.service);
              citedSources.push({
                id: source.id,
                fileName: source.name,
                link: source.url ?? '',
                sensitivityLabelId: (source as { sensitivityLabelId?: string })?.sensitivityLabelId,
                sensitivityLabel: (source as { sensitivityLabel?: { id?: string; name?: string } })?.sensitivityLabel,
                resourceType,
              });
            }
            break;
          case AiService.copilotStudio:
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
              value: step.ragQuery,
            });
            if (step.sensitivityLabelId) {
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.sensitivityLabelFieldName'),
                value: step.sensitivityLabelId,
                isCopyable: true,
                copyableText: step.sensitivityLabelId,
              });
            }
            for (const source of step.citedSources) {
              const resourceType = resolveCitationResourceType(source, step.service);
              citedSources.push({
                id: source.id,
                fileName: source.name,
                link: source.url ?? '',
                sensitivityLabelId: (source as { sensitivityLabelId?: string })?.sensitivityLabelId,
                sensitivityLabel: (source as { sensitivityLabel?: { id?: string; name?: string } })?.sensitivityLabel,
                resourceType,
                sensitiveData: {
                  buttonText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchCitationSnippet'),
                  isRawValue: true,
                  showPopupBtn: true,
                  sensitiveDataMethod: () =>
                    this._aiAgentStepsService.getAgentStepSensitiveData(
                      step.integration.id,
                      step.id,
                      SensitiveDataRequestActions.snippet,
                      source.id,
                      this._getFirstFindingIdForStep(step.id)
                    ),
                },
              });
            }
            if (step.sources) {
              for (const source of step.sources) {
                const resourceType = resolveCitationResourceType(source, step.service);
                sources.push({
                  id: source.id,
                  fileName: source.name ?? source.id,
                  resourceType,
                  link: source.url ?? '',
                  sensitivityLabelId: (source as { sensitivityLabelId?: string })?.sensitivityLabelId,
                  sensitivityLabel: (source as { sensitivityLabel?: { id?: string; name?: string } })?.sensitivityLabel,
                });
              }
            }
            break;
          case AiService.chatGptEnterprise:
          case AiService.azureAiFoundry:
            for (const source of step.citedSources) {
              const resourceType = resolveCitationResourceType(source, step.service);
              citedSources.push({
                id: source.id,
                fileName: source.name,
                link: source.url ?? '',
                resourceType,
              });
            }
            break;
          case AiService.vertexAi:
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
              value: '',
              sensitiveData: {
                type: SensitiveDataType.SNIPPET,
              },
            });

            for (let index = 0; index < step.citedSources.length; index++) {
              const source = step.citedSources[index];
              const resourceType = resolveCitationResourceType(source, step.service);
              const citedSource: CitedSource = {
                id: source.id,
                fileName: source.name,
                link: source.url ?? '',
                name: source.name,
                resourceType,
              };

              const shouldShowFetchButton = source.contentAvailable;

              if (shouldShowFetchButton) {
                citedSource.sensitiveData = {
                  buttonText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchCitationSnippet'),
                  isRawValue: true,
                  showPopupBtn: true,
                  sensitiveDataMethod: () =>
                    this._aiAgentStepsService.getAgentStepSensitiveData(
                      step.integration.id,
                      step.id,
                      SensitiveDataRequestActions.snippet,
                      source.id,
                      this._getFirstFindingIdForStep(step.id)
                    ),
                };
              }

              citedSources.push(citedSource);
            }
            break;
        }
        break;
      case StepType.triggerStep:
        switch (step.service) {
          case AiService.copilotStudio:
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.fetchPayload'),
              value: '',
              sensitiveData: {
                type: SensitiveDataType.SNIPPET,
              },
            });
            if (step.triggerDisplayName)
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.triggeringEvent'),
                value: step.triggerDisplayName,
                iconPath: step.triggerIconUrl,
              });
            break;
        }
        break;
      case StepType.toolInvocationStep:
        // Check if this is inline mode - if so, use inline tool invocation types
        const isInlineMode = '_inlineMode' in step && step._inlineMode === true;

        if (isInlineMode) {
          // Inline mode: use inline-specific sensitive data types for params and result
          messages.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
            value: '',
            sensitiveData: {
              type: SensitiveDataType.TOOL_INVOCATION_PARAMS,
            },
          });
          if (step.toolInvocations.length > 0 && step.toolInvocations[0].toolDisplayName) {
            const toolMessage: ActivityMessage = {
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
              value: step.toolInvocations[0].toolDisplayName,
              iconPath: 'triggerIconUrl' in step ? step.triggerIconUrl : undefined,
            };
            const toolInventoryId = step.toolInvocations[0].toolId;
            if (toolInventoryId != null && toolInventoryId.trim() !== '') {
              toolMessage.method = () => {
                this._sidePanel.openResourceDetailsFromIssue(toolInventoryId, step.integration.id);
              };
            }
            messages.push(toolMessage);
          }
          if (step.toolInvocations.length > 0 && step.toolInvocations[0].toolOrigin)
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
              value: step.toolInvocations[0].toolOrigin,
            });
          messages.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
            value: '',
            sensitiveData: {
              type: SensitiveDataType.TOOL_INVOCATION_RESULT,
            },
          });
        } else {
          // Regular mode: use service-specific handling
          switch (step.service) {
            case AiService.copilotStudio:
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
                value: '',
                sensitiveData: {
                  type: SensitiveDataType.TOOL_INVOCATION_PARAMS,
                  resourceId: step.toolInvocations[0].toolPlatformInfo?.resourceId ?? '',
                },
              });
              if (step.toolInvocations.length > 0 && step.toolInvocations[0].toolDisplayName) {
                const toolMessage: ActivityMessage = {
                  key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
                  value: step.toolInvocations[0].toolDisplayName,
                  iconPath: step.triggerIconUrl,
                };
                const toolInventoryId = step.toolInvocations[0].toolId;
                if (toolInventoryId != null && toolInventoryId.trim() !== '') {
                  toolMessage.method = () => {
                    this._sidePanel.openResourceDetailsFromIssue(toolInventoryId, step.integration.id);
                  };
                }
                messages.push(toolMessage);
              }
              if (step.toolInvocations.length > 0 && step.toolInvocations[0].toolOrigin)
                messages.push({
                  key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
                  value: step.toolInvocations[0].toolOrigin,
                });
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
                value: '',
                sensitiveData: {
                  type: SensitiveDataType.TOOL_INVOCATION_RESULT,
                  resourceId: step.toolInvocations[0].toolPlatformInfo?.resourceId ?? '',
                },
              });
              break;
            case AiService.chatGptEnterprise:
            case AiService.azureAiFoundry:
            case AiService.vertexAi:
            case AiService.bedrockAgentCore:
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
                value: '',
                sensitiveData: {
                  type: SensitiveDataType.TOOL_INVOCATION_PARAMS,
                  resourceId: step.toolInvocations[0].toolPlatformInfo?.resourceId ?? '',
                },
              });
              if (step.toolInvocations.length > 0 && step.toolInvocations[0].toolDisplayName) {
                const toolMessage: ActivityMessage = {
                  key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
                  value: step.toolInvocations[0].toolDisplayName,
                };
                const toolInventoryId = step.toolInvocations[0].toolId;
                if (toolInventoryId != null && toolInventoryId.trim() !== '') {
                  toolMessage.method = () => {
                    this._sidePanel.openResourceDetailsFromIssue(toolInventoryId, step.integration.id);
                  };
                }
                messages.push(toolMessage);
              }
              if (step.toolInvocations.length > 0 && step.toolInvocations[0].toolOrigin)
                messages.push({
                  key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
                  value: step.toolInvocations[0].toolOrigin,
                });
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
                value: '',
                sensitiveData: {
                  type: SensitiveDataType.TOOL_INVOCATION_RESULT,
                  resourceId: step.toolInvocations[0].toolPlatformInfo?.resourceId ?? '',
                },
              });
              break;
          }
        }
        break;
      case StepType.toolInvocationResultStep:
        // Handle tool invocation result step
        const isInlineModeResult = '_inlineMode' in step && step._inlineMode === true;

        if (isInlineModeResult) {
          // Inline mode: use inline-specific sensitive data type for result
          if (step.toolInvocations && step.toolInvocations.length > 0 && step.toolInvocations[0].toolDisplayName) {
            const iconPath =
              'triggerIconUrl' in step && typeof (step as { triggerIconUrl?: string }).triggerIconUrl === 'string'
                ? (step as { triggerIconUrl: string }).triggerIconUrl
                : undefined;
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
              value: step.toolInvocations[0].toolDisplayName,
              iconPath,
            });
          }
          if (step.toolInvocations && step.toolInvocations.length > 0 && step.toolInvocations[0].toolOrigin)
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
              value: step.toolInvocations[0].toolOrigin,
            });
          messages.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
            value: '',
            sensitiveData: {
              type: SensitiveDataType.TOOL_INVOCATION_RESULT,
            },
          });
        } else {
          // Regular mode: use service-specific handling
          if (step.toolInvocations && step.toolInvocations.length > 0) {
            if (step.toolInvocations[0].toolDisplayName) {
              const iconPath =
                'triggerIconUrl' in step && typeof (step as { triggerIconUrl?: string }).triggerIconUrl === 'string'
                  ? (step as { triggerIconUrl: string }).triggerIconUrl
                  : undefined;
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
                value: step.toolInvocations[0].toolDisplayName,
                iconPath,
              });
            }
            if (step.toolInvocations[0].toolOrigin)
              messages.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
                value: step.toolInvocations[0].toolOrigin,
              });
          }
          messages.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
            value: '',
            sensitiveData: {
              type: SensitiveDataType.TOOL_INVOCATION_RESULT,
              resourceId: step.toolInvocations?.[0]?.toolPlatformInfo?.resourceId ?? '',
            },
          });
        }
        break;
      case StepType.toolInvocationRequestStep:
        messages.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
          value: '',
          sensitiveData: {
            type: SensitiveDataType.TOOL_INVOCATION_PARAMS,
          },
        });
        if (step.toolInvocations?.length > 0 && step.toolInvocations[0].toolDisplayName) {
          const toolMessage: ActivityMessage = {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
            value: step.toolInvocations[0].toolDisplayName,
          };
          const toolInventoryId = step.toolInvocations[0].toolId;
          if (toolInventoryId != null && toolInventoryId.trim() !== '') {
            toolMessage.method = () => {
              this._sidePanel.openResourceDetailsFromIssue(toolInventoryId, step.integration.id);
            };
          }
          messages.push(toolMessage);
        }
        if (step.toolInvocations?.length > 0 && step.toolInvocations[0].toolOrigin)
          messages.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
            value: step.toolInvocations[0].toolOrigin,
          });
        break;
      case StepType.toolRequestStep: {
        const toolReqs = (step as unknown as ToolRequestStep).toolInvocationRequests;
        messages.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
          value: '',
          sensitiveData: {
            type: SensitiveDataType.TOOL_INVOCATION_PARAMS,
            resourceId: toolReqs?.[0]?.toolPlatformInfo?.resourceId ?? '',
          },
        });
        if (toolReqs?.length > 0 && toolReqs[0].toolDisplayName) {
          const toolMessage: ActivityMessage = {
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
            value: toolReqs[0].toolDisplayName,
          };
          const toolInventoryId = toolReqs[0].toolId;
          if (toolInventoryId != null && toolInventoryId.trim() !== '') {
            toolMessage.method = () => {
              this._sidePanel.openResourceDetailsFromIssue(toolInventoryId, step.integration.id);
            };
          }
          messages.push(toolMessage);
        }
        if (toolReqs?.length > 0 && toolReqs[0].toolOrigin)
          messages.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
            value: toolReqs[0].toolOrigin,
          });
        break;
      }
      case StepType.toolResponseStep: {
        const toolResps = (step as unknown as ToolResponseStep).toolInvocationResponses;
        if (toolResps?.length > 0) {
          if (toolResps[0].toolDisplayName) {
            const iconPath =
              'triggerIconUrl' in step && typeof (step as { triggerIconUrl?: string }).triggerIconUrl === 'string'
                ? (step as { triggerIconUrl: string }).triggerIconUrl
                : undefined;
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
              value: toolResps[0].toolDisplayName,
              iconPath,
            });
          }
          if (toolResps[0].toolOrigin)
            messages.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolOriginFieldName'),
              value: toolResps[0].toolOrigin,
            });
        }
        messages.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
          value: '',
          sensitiveData: {
            type: SensitiveDataType.TOOL_INVOCATION_RESULT,
            resourceId: toolResps?.[0]?.toolPlatformInfo?.resourceId ?? '',
          },
        });
        break;
      }
    }

    if (messages.length === 0 && step.id.startsWith('rsa-')) {
      messages.push({
        key: '',
        value: '',
        sensitiveData: {
          type: SensitiveDataType.SNIPPET,
        },
      });
    }

    return { messages, citedSources, sources };
  }
}
